import { useCallback, useLayoutEffect, useState } from "react";
import type { CSSProperties, RefObject } from "react";

const MARGIN = 8;
const GAP = 6;

/** Where a `position: fixed` panel goes for the element that opened it: under it, or above
 * when there is more room there, kept on screen and capped to the room it has. The panel's
 * width is set in CSS, so the height read here is the real one. `nudge` moves it left of
 * the anchor's edge. Call `position` again when the anchor may have moved. */
export const useAnchoredPlacement = (
  open: boolean,
  anchor: RefObject<HTMLElement>,
  panel: RefObject<HTMLElement>,
  nudge = 0,
) => {
  const [place, setPlace] = useState<CSSProperties>({ visibility: "hidden" });
  const position = useCallback(() => {
    const box = panel.current;
    const at = anchor.current?.getBoundingClientRect();
    if (!at || !box) return;
    const left = Math.min(
      Math.max(MARGIN, at.left - nudge),
      window.innerWidth - box.offsetWidth - MARGIN,
    );
    const below = window.innerHeight - at.bottom - GAP - MARGIN;
    const above = at.top - GAP - MARGIN;
    const height = Math.min(box.scrollHeight, Math.max(below, above));
    const top = height <= below || below >= above ? at.bottom + GAP : at.top - GAP - height;
    setPlace({ top, left, maxHeight: Math.max(below, above) });
  }, [anchor, panel, nudge]);

  useLayoutEffect(() => {
    if (!open) return;
    position();
    // Content that changes while it is open (a refetch, a calendar) re-places it.
    const observer = new ResizeObserver(position);
    if (panel.current) observer.observe(panel.current);
    return () => observer.disconnect();
  }, [open, position, panel]);

  return { place, position };
};
